/**
 * 鉴权 Context：登录后 user_id/token 全局共享；登出清空。
 * 设计：CLAUDE.md §前端规范 §状态管理 → 跨组件共享用 React Context + useReducer。
 *
 * 持久化：token / userId 存 localStorage（2026-10-28 起，原 sessionStorage）——
 * 关浏览器/标签页不掉线，免登录时长由服务端 30 天 TTL 决定
 * （src/services/auth_session.py AuthSessionStore.DEFAULT_TTL_SECONDS）。
 *
 * 编辑权限：登录后（或已有 token 的 mount）异步拉取 GET /api/v1/auth/permissions，
 * 存 editable_pages / is_admin；canEdit(pageKey) 供各页面禁用无权限的编辑 UI。
 * 未加载完成（editablePages=null）时 canEdit 返回 false（安全默认，按钮短暂禁用至就绪）。
 * 强制执行仅在前端（后端写接口不加 403，详见 docs 需求变更摘要）。
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { getMyPermissions, logout as apiLogout } from "../services/auth";
import { getToken, setToken } from "../services/apiClient";

interface AuthState {
  token: string | null;
  userId: string | null;
  /** null=未加载；Set<string>=已加载（可能为空）；管理员在此为空集，由 is_admin 放行 */
  editablePages: Set<string> | null;
  isAdmin: boolean;
}

interface AuthContextValue extends AuthState {
  login: (token: string, userId: string) => void;
  logout: () => Promise<void>;
  /** 当前用户是否可编辑指定页面（未加载完成时返回 false 兜底） */
  canEdit: (pageKey: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const USER_KEY = "ad_tech_user_id";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => ({
    token: getToken(),
    userId: localStorage.getItem(USER_KEY),
    editablePages: null,
    isAdmin: false,
  }));

  // 拉取当前用户编辑权限；失败 → 置空集（deny 兜底），不抛错打断页面。
  const fetchPermissions = useCallback(async () => {
    try {
      const p = await getMyPermissions();
      setState((s) => ({
        ...s,
        editablePages: new Set(p.editable_pages),
        isAdmin: p.is_admin,
      }));
    } catch {
      setState((s) => ({ ...s, editablePages: new Set(), isAdmin: false }));
    }
  }, []);

  const login = useCallback(
    (token: string, userId: string) => {
      setToken(token);
      localStorage.setItem(USER_KEY, userId);
      setState({ token, userId, editablePages: null, isAdmin: false });
      // 异步拉取权限，不阻塞登录跳转
      void fetchPermissions();
    },
    [fetchPermissions],
  );

  const logout = useCallback(async () => {
    await apiLogout();
    localStorage.removeItem(USER_KEY);
    setState({
      token: null,
      userId: null,
      editablePages: null,
      isAdmin: false,
    });
  }, []);

  // 已登录（有 token）→ 首次挂载时拉取权限
  useEffect(() => {
    if (getToken()) {
      void fetchPermissions();
    }
  }, [fetchPermissions]);

  // 跨 tab：监听 storage 事件，保持 token / 权限一致
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== USER_KEY && e.key !== "ad_tech_token") return;
      const token = getToken();
      const userId = localStorage.getItem(USER_KEY);
      if (token) {
        setState((s) => ({ ...s, token, userId }));
        void fetchPermissions();
      } else {
        setState({
          token: null,
          userId: null,
          editablePages: null,
          isAdmin: false,
        });
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [fetchPermissions]);

  const canEdit = useCallback(
    (pageKey: string) => {
      if (state.isAdmin) return true;
      return state.editablePages?.has(pageKey) ?? false;
    },
    [state.isAdmin, state.editablePages],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout, canEdit }),
    [state, login, logout, canEdit],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
}
